import { Injectable } from '@angular/core';
import { ReelBanner } from '../models/reel.model';

/**
 * Warms the first Feather Reel clip once the menu has loaded, so the reel starts fast when tapped —
 * the web analogue of iOS `MenuHomeViewModel.triggerReelPreloadIfNeeded`.
 *
 * Best-effort by design: Chromium (Android/desktop) honours `preload="auto"` and fetches eagerly;
 * iOS Safari may defer video buffering until playback/gesture, so the video warm mainly benefits
 * Android/desktop. Posters are warmed via `new Image()` (the pattern used by product-list-item),
 * which is reliable everywhere. Idempotent per banner; a new banner (venue switch) replaces the warm.
 */
@Injectable({ providedIn: 'root' })
export class ReelPreloadService {
  private warmedBannerId: string | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private warmedImages: HTMLImageElement[] = [];

  warm(banner: ReelBanner | null): void {
    if (!banner || banner.clips.length === 0) return;
    if (this.warmedBannerId === banner.bannerId) return; // already warmed this banner
    this.clear();
    this.warmedBannerId = banner.bannerId;

    const first = banner.clips[0];

    // Warm the first video's data into the browser's media cache via a detached element.
    if (first.isVideo && first.mediaUrl) {
      const v = document.createElement('video');
      v.muted = true;
      v.setAttribute('muted', '');
      v.playsInline = true;
      v.preload = 'auto';
      v.src = first.mediaUrl;
      // Off-screen but ATTACHED (display:none can suspend the fetch); kept referenced so it isn't GC'd.
      v.style.cssText =
        'position:absolute;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(v);
      try {
        v.load();
      } catch {
        /* ignore */
      }
      this.videoEl = v;
    }

    // Warm the first couple of posters (reliable on every browser).
    for (const clip of banner.clips.slice(0, 2)) {
      if (clip.thumbnailUrl) {
        const img = new Image();
        img.src = clip.thumbnailUrl;
        this.warmedImages.push(img);
      }
    }
  }

  /** Release the detached warm-up video (called automatically when a new banner is warmed). */
  clear(): void {
    if (this.videoEl) {
      try {
        this.videoEl.pause();
        this.videoEl.removeAttribute('src');
        this.videoEl.load();
        this.videoEl.remove();
      } catch {
        /* ignore */
      }
      this.videoEl = null;
    }
    this.warmedImages = [];
    this.warmedBannerId = null;
  }
}
